import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from "react";
import MarkdownIt from "markdown-it";
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from "recharts";
import { sbFetch } from "../utils/supabaseClient";
import { useToast } from "./Toast";
import { SVG_ICONS } from "./TablerIconPicker";

const ContentPlanningTab = forwardRef((props, ref) => {
  const { success, error } = useToast();
  const md = useMemo(() => {
    const mdInstance = new MarkdownIt({ html: false, breaks: true });

    // Custom renderer for inline code to handle bold/italic properly
    const defaultRender = mdInstance.renderer.rules.text;
    mdInstance.renderer.rules.text = function(tokens, idx) {
      return defaultRender(tokens, idx);
    };

    return mdInstance;
  }, []);

  // Helper to render markdown content with checkbox support
  const renderMarkdownWithCheckbox = (text) => {
    // Check for checkbox syntax: [ ] or [x]
    const checkboxMatch = text.match(/^\s*\[(.)\]\s*(.*)/);
    if (checkboxMatch) {
      const isChecked = checkboxMatch[1].toLowerCase() === 'x';
      const content = checkboxMatch[2];
      const htmlContent = md.renderInline(content);
      return `<span style="display: flex; align-items: center; gap: 6px;"><input type="checkbox" ${isChecked ? 'checked' : ''} style="cursor: pointer; width: 14px; height: 14px; accent-color: #22C55E;" /><span>${htmlContent}</span></span>`;
    }
    return md.renderInline(text);
  };
  const [activeTab, setActiveTab] = useState("list");
  const [mdContent, setMdContent] = useState("");
  const [preview, setPreview] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    loadChecklist();
  }, []);

  useImperativeHandle(ref, () => ({
    loadChecklist
  }));

  const loadChecklist = async () => {
    try {
      setLoading(true);
      const response = await sbFetch("GET", "content_checklist?order=created_at.asc");
      setChecklist(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error("Error loading checklist:", err);
      error("Failed to load checklist");
    } finally {
      setLoading(false);
    }
  };

  const parseMD = (text) => {
    const items = [];
    let currentType = "";
    let currentCategory = "";

    const lines = text.split("\n").filter(line => line.trim());

    lines.forEach(line => {
      if (line.startsWith("## ")) {
        currentType = line.replace("## ", "").trim();
      } else if (line.startsWith("### ")) {
        currentCategory = line.replace("### ", "").trim();
      } else if (line.startsWith("- ")) {
        const title = line.replace("- ", "").trim();
        if (currentType && title) {
          items.push({
            type: currentType,
            category: currentCategory || "General",
            title: title
          });
        }
      }
    });

    return items;
  };

  const handleParsePreview = () => {
    const parsed = parseMD(mdContent);
    setPreview(parsed);
    setShowPreview(true);
    // Scroll to show preview
    setTimeout(() => {
      const previewSection = document.querySelector('[style*="background: rgba(34, 197, 94)"]');
      if (previewSection) {
        previewSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleImport = async () => {
    if (preview.length === 0) {
      error("No items to import. Parse the MD first.");
      return;
    }

    setImporting(true);
    try {
      await sbFetch("POST", "content_checklist", preview.map(item => ({
        type: item.type,
        category: item.category,
        title: item.title,
        completed: false
      })));

      success(`Imported ${preview.length} items`);
      setMdContent("");
      setPreview([]);
      setShowPreview(false);
      loadChecklist();
      setActiveTab("list");
    } catch (err) {
      console.error("Error importing checklist:", err);
      error("Failed to import checklist");
    } finally {
      setImporting(false);
    }
  };

  const handleToggleComplete = async (id, completed) => {
    try {
      await sbFetch("PATCH", `content_checklist?id=eq.${id}`, { completed: !completed });
      // Update local state without reloading to avoid jumping to top
      setChecklist(checklist.map(item =>
        item.id === id ? { ...item, completed: !completed } : item
      ));
      success(!completed ? "Marked complete" : "Marked incomplete");
    } catch (err) {
      console.error("Error updating item:", err);
      error("Failed to update item");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;

    try {
      await sbFetch("DELETE", `content_checklist?id=eq.${id}`);
      loadChecklist();
      success("Item deleted");
    } catch (err) {
      console.error("Error deleting item:", err);
      error("Failed to delete item");
    }
  };

  const handleCopyTitle = (title, type) => {
    const textToCopy = type ? `${type}: ${title}` : title;
    navigator.clipboard.writeText(textToCopy).then(() => {
      success("Copied to clipboard");
    }).catch(() => {
      error("Failed to copy");
    });
  };

  // Get unique types and categories
  const uniqueTypes = [...new Set(checklist.map(item => item.type))].sort();
  const uniqueCategories = [...new Set(checklist.map(item => item.category))].sort();

  // Filter by search text
  const filteredBySearch = checklist.filter(item =>
    item.title.toLowerCase().includes(searchText.toLowerCase())
  );

  // Group checklist by type and category
  let groupedChecklist = {};
  filteredBySearch.forEach(item => {
    if (!groupedChecklist[item.type]) {
      groupedChecklist[item.type] = {};
    }
    if (!groupedChecklist[item.type][item.category]) {
      groupedChecklist[item.type][item.category] = [];
    }
    groupedChecklist[item.type][item.category].push(item);
  });

  // Filter by selected type
  if (selectedType && groupedChecklist[selectedType]) {
    groupedChecklist = { [selectedType]: groupedChecklist[selectedType] };
  } else if (selectedType) {
    groupedChecklist = {};
  }

  // Filter by selected category
  if (selectedCategory) {
    const filtered = {};
    Object.entries(groupedChecklist).forEach(([type, categories]) => {
      if (categories[selectedCategory]) {
        filtered[type] = { [selectedCategory]: categories[selectedCategory] };
      }
    });
    groupedChecklist = filtered;
  }

  // Calculate metrics
  const totalOpen = checklist.filter(item => !item.completed).length;
  const totalCompleted = checklist.filter(item => item.completed).length;
  const donutData = [
    { name: "Open", value: totalOpen, fill: "#F59E0B" },
    { name: "Completed", value: totalCompleted, fill: "#22C55E" }
  ];

  // Category breakdown metrics
  const categoryMetrics = {};
  checklist.forEach(item => {
    const key = item.category;
    if (!categoryMetrics[key]) {
      categoryMetrics[key] = { category: key, open: 0, completed: 0 };
    }
    if (item.completed) {
      categoryMetrics[key].completed++;
    } else {
      categoryMetrics[key].open++;
    }
  });
  const categoryArray = Object.values(categoryMetrics).sort((a, b) => b.open + b.completed - (a.open + a.completed));

  if (loading) return <div className="loading">Loading checklist...</div>;

  return (
    <div style={{ padding: "20px", color: "#e0e0e0" }}>
      {/* Tabs */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "24px", borderBottom: "1px solid rgba(59, 130, 246, 0.2)", paddingBottom: "12px" }}>
        <button
          onClick={() => setActiveTab("list")}
          style={{
            padding: "10px 16px",
            background: activeTab === "list" ? "rgba(59, 130, 246, 0.2)" : "transparent",
            border: activeTab === "list" ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid transparent",
            color: activeTab === "list" ? "#5B9EF0" : "#7A9CC4",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "600",
            transition: "all 0.2s"
          }}
        >
          Checklist
        </button>
        <button
          onClick={() => setActiveTab("metrics")}
          style={{
            padding: "10px 16px",
            background: activeTab === "metrics" ? "rgba(59, 130, 246, 0.2)" : "transparent",
            border: activeTab === "metrics" ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid transparent",
            color: activeTab === "metrics" ? "#5B9EF0" : "#7A9CC4",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "600",
            transition: "all 0.2s"
          }}
        >
          Metrics
        </button>
        <button
          onClick={() => setActiveTab("import")}
          style={{
            padding: "10px 16px",
            background: activeTab === "import" ? "rgba(59, 130, 246, 0.2)" : "transparent",
            border: activeTab === "import" ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid transparent",
            color: activeTab === "import" ? "#5B9EF0" : "#7A9CC4",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "600",
            transition: "all 0.2s"
          }}
        >
          Import
        </button>
      </div>

      {/* Checklist Tab */}
      {activeTab === "list" && (
        <div style={{ background: "rgba(59, 130, 246, 0.05)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", padding: "20px" }}>
          <h3 style={{ margin: "0 0 16px 0", color: "#5B9EF0" }}>
            Content Checklist ({checklist.filter(item => !item.completed).length})
          </h3>

          {/* Search */}
          <div style={{ marginBottom: "16px" }}>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search items..."
              style={{
                width: "100%",
                padding: "10px 12px",
                background: "rgba(5, 14, 26, 0.8)",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                borderRadius: "6px",
                color: "#e0e0e0",
                fontSize: "13px",
                boxSizing: "border-box"
              }}
            />
          </div>

          {/* Content Type Filters */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
            <button
              onClick={() => setSelectedType(null)}
              style={{
                padding: "6px 14px",
                background: selectedType === null ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
                color: selectedType === null ? "#5B9EF0" : "#7A9CC4",
                border: selectedType === null ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
                transition: "all 0.2s"
              }}
            >
              All
            </button>
            {uniqueTypes.map(type => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                style={{
                  padding: "6px 14px",
                  background: selectedType === type ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
                  color: selectedType === type ? "#5B9EF0" : "#7A9CC4",
                  border: selectedType === type ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                  borderRadius: "20px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: "500",
                  transition: "all 0.2s"
                }}
              >
                {type}
              </button>
            ))}
          </div>

          {/* Category Filter Dropdown */}
          <div style={{ marginBottom: "20px" }}>
            <select
              value={selectedCategory || ""}
              onChange={(e) => setSelectedCategory(e.target.value || null)}
              style={{
                padding: "10px 12px",
                background: "rgba(5, 14, 26, 0.8)",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                borderRadius: "6px",
                color: "#e0e0e0",
                fontSize: "13px",
                cursor: "pointer",
                fontWeight: "500",
                minWidth: "200px"
              }}
            >
              <option value="">All Categories</option>
              {uniqueCategories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {checklist.length === 0 ? (
            <div style={{ color: "#7A9CC4", textAlign: "center", padding: "40px 20px" }}>
              No items yet. Go to Import tab to add from MD.
            </div>
          ) : Object.keys(groupedChecklist).length === 0 ? (
            <div style={{ color: "#7A9CC4", textAlign: "center", padding: "40px 20px" }}>
              No items match your search.
            </div>
          ) : (
            <div>
              {Object.entries(groupedChecklist).map(([type, categories]) => (
                <div key={type} style={{ marginBottom: "24px" }}>
                  <div style={{ color: "#5B9EF0", fontWeight: "600", fontSize: "14px", marginBottom: "12px", borderBottom: "1px solid rgba(59, 130, 246, 0.2)", paddingBottom: "8px" }}>
                    {type}
                  </div>
                  {Object.entries(categories).map(([category, items]) => (
                    <div key={category} style={{ marginBottom: "16px" }}>
                      <div style={{ color: "#7A9CC4", fontSize: "12px", fontWeight: "600", marginBottom: "8px" }}>{category}</div>
                      <div style={{ marginLeft: "12px" }}>
                        {items.map(item => (
                          <div
                            key={item.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              padding: "10px",
                              background: item.completed ? "rgba(22, 163, 74, 0.05)" : "transparent",
                              borderRadius: "4px",
                              marginBottom: "8px",
                              borderLeft: item.completed ? "2px solid rgba(34, 197, 94, 0.5)" : "2px solid transparent"
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={item.completed || false}
                              onChange={() => handleToggleComplete(item.id, item.completed)}
                              style={{
                                cursor: "pointer",
                                width: "18px",
                                height: "18px",
                                accentColor: "#22C55E"
                              }}
                            />
                            <span
                              style={{
                                flex: 1,
                                color: item.completed ? "#7A9CC4" : "#e0e0e0",
                                textDecoration: item.completed ? "line-through" : "none",
                                fontSize: "13px"
                              }}
                              dangerouslySetInnerHTML={{ __html: renderMarkdownWithCheckbox(item.title) }}
                            />
                            <button
                              onClick={() => handleCopyTitle(item.title, type)}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "#5B9EF0",
                                cursor: "pointer",
                                padding: "4px",
                                display: "flex",
                                alignItems: "center"
                              }}
                              title={`Copy "${type}: ${item.title}"`}
                            >
                              {SVG_ICONS["copy"]({ size: 16, color: "#5B9EF0" })}
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "#F87171",
                                cursor: "pointer",
                                padding: "4px",
                                display: "flex",
                                alignItems: "center"
                              }}
                              title="Delete"
                            >
                              {SVG_ICONS["trash"]({ size: 16, color: "#F87171" })}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Metrics Tab */}
      {activeTab === "metrics" && (
        <div style={{ background: "rgba(59, 130, 246, 0.05)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", padding: "20px" }}>
          <h3 style={{ margin: "0 0 24px 0", color: "#5B9EF0" }}>Content Planning Metrics</h3>

          {checklist.length === 0 ? (
            <div style={{ color: "#7A9CC4", textAlign: "center", padding: "40px 20px" }}>
              No items yet. Go to Import tab to add content.
            </div>
          ) : (
            <div>
              {/* Donut Chart */}
              <div style={{ marginBottom: "40px", background: "rgba(0, 0, 0, 0.2)", borderRadius: "8px", padding: "20px" }}>
                <h4 style={{ margin: "0 0 16px 0", color: "#7A9CC4", fontSize: "13px", fontWeight: "600" }}>Overall Progress</h4>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={donutData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {donutData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: "rgba(5, 14, 26, 0.95)",
                          border: "1px solid rgba(59, 130, 246, 0.3)",
                          borderRadius: "6px",
                          color: "#e0e0e0"
                        }}
                        formatter={(value) => [`${value} items`, '']}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        wrapperStyle={{ color: "#7A9CC4", fontSize: "13px" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ display: "flex", gap: "40px", justifyContent: "center", marginTop: "16px" }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "24px", fontWeight: "700", color: "#F59E0B" }}>{totalOpen}</div>
                    <div style={{ fontSize: "12px", color: "#7A9CC4", marginTop: "4px" }}>Open</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "24px", fontWeight: "700", color: "#22C55E" }}>{totalCompleted}</div>
                    <div style={{ fontSize: "12px", color: "#7A9CC4", marginTop: "4px" }}>Completed</div>
                  </div>
                </div>
              </div>

              {/* Category Breakdown Cards */}
              <h4 style={{ margin: "0 0 16px 0", color: "#7A9CC4", fontSize: "13px", fontWeight: "600" }}>By Category</h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "12px" }}>
                {categoryArray.map(cat => {
                  const completionRate = cat.open + cat.completed > 0
                    ? Math.round((cat.completed / (cat.open + cat.completed)) * 100)
                    : 0;
                  return (
                    <div
                      key={cat.category}
                      style={{
                        background: "rgba(59, 130, 246, 0.08)",
                        border: "1px solid rgba(59, 130, 246, 0.2)",
                        borderRadius: "8px",
                        padding: "16px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px"
                      }}
                    >
                      <div>
                        <div style={{ color: "#e0e0e0", fontWeight: "600", fontSize: "14px", marginBottom: "8px" }}>
                          {cat.category}
                        </div>
                        <div style={{ display: "flex", gap: "16px", fontSize: "13px" }}>
                          <div>
                            <span style={{ color: "#7A9CC4" }}>Open: </span>
                            <span style={{ color: "#F59E0B", fontWeight: "600" }}>{cat.open}</span>
                          </div>
                          <div>
                            <span style={{ color: "#7A9CC4" }}>Done: </span>
                            <span style={{ color: "#22C55E", fontWeight: "600" }}>{cat.completed}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                          <span style={{ fontSize: "12px", color: "#7A9CC4" }}>Progress</span>
                          <span style={{ fontSize: "12px", color: "#5B9EF0", fontWeight: "600" }}>{completionRate}%</span>
                        </div>
                        <div style={{ height: "4px", background: "rgba(59, 130, 246, 0.1)", borderRadius: "2px", overflow: "hidden" }}>
                          <div
                            style={{
                              height: "100%",
                              background: "linear-gradient(90deg, #F59E0B 0%, #22C55E 100%)",
                              width: `${completionRate}%`,
                              transition: "width 0.3s ease"
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Import Tab */}
      {activeTab === "import" && (
        <div style={{ background: "rgba(59, 130, 246, 0.05)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", padding: "20px" }}>
          <h3 style={{ margin: "0 0 16px 0", color: "#5B9EF0" }}>Bulk Import from MD</h3>
          <p style={{ fontSize: "12px", color: "#7A9CC4", marginBottom: "12px" }}>
            Paste your markdown with this format:
          </p>
          <pre style={{ background: "rgba(0, 0, 0, 0.3)", padding: "12px", borderRadius: "4px", fontSize: "11px", color: "#9DB4D4", overflow: "auto", marginBottom: "16px" }}>
{`## Lessons
### AI Adoption
- Change management basics
- Leadership alignment

### Strategy
- Market analysis

## Prompts
### Workflows
- Email automation`}
          </pre>
          <textarea
            value={mdContent}
            onChange={(e) => setMdContent(e.target.value)}
            placeholder="Paste your MD checklist here..."
            style={{
              width: "100%",
              minHeight: "200px",
              padding: "12px",
              background: "rgba(5, 14, 26, 0.8)",
              border: "1px solid rgba(59, 130, 246, 0.3)",
              borderRadius: "6px",
              color: "#e0e0e0",
              fontFamily: "monospace",
              fontSize: "12px",
              boxSizing: "border-box",
              marginBottom: "12px"
            }}
          />
          <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
            <button
              onClick={handleParsePreview}
              disabled={!mdContent.trim()}
              style={{
                padding: "10px 16px",
                background: mdContent.trim() ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                color: mdContent.trim() ? "#5B9EF0" : "#7A9CC4",
                borderRadius: "6px",
                cursor: mdContent.trim() ? "pointer" : "not-allowed",
                fontSize: "13px",
                fontWeight: "600",
                transition: "all 0.2s"
              }}
            >
              Parse & Preview
            </button>
            {showPreview && (
              <button
                onClick={handleImport}
                disabled={importing}
                style={{
                  padding: "10px 16px",
                  background: "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)",
                  border: "none",
                  color: "white",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: "600",
                  transition: "all 0.2s",
                  opacity: importing ? 0.6 : 1
                }}
              >
                {importing ? "Importing..." : `Import ${preview.length} Items`}
              </button>
            )}
          </div>

          {/* Preview Section */}
          {showPreview && preview.length > 0 && (
            <div style={{ background: "rgba(34, 197, 94, 0.05)", border: "1px solid rgba(34, 197, 94, 0.2)", borderRadius: "8px", padding: "16px" }}>
              <h4 style={{ margin: "0 0 12px 0", color: "#22C55E" }}>Preview ({preview.length} items)</h4>
              <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                {Object.entries(
                  preview.reduce((acc, item) => {
                    if (!acc[item.type]) acc[item.type] = {};
                    if (!acc[item.type][item.category]) acc[item.type][item.category] = [];
                    acc[item.type][item.category].push(item);
                    return acc;
                  }, {})
                ).map(([type, categories]) => (
                  <div key={type} style={{ marginBottom: "12px" }}>
                    <div style={{ color: "#22C55E", fontWeight: "600", fontSize: "12px", marginBottom: "6px" }}>{type}</div>
                    {Object.entries(categories).map(([category, items]) => (
                      <div key={category} style={{ marginLeft: "12px", marginBottom: "6px" }}>
                        <div style={{ color: "#7A9CC4", fontSize: "11px", marginBottom: "3px" }}>{category}</div>
                        <ul style={{ margin: "0", paddingLeft: "16px", color: "#e0e0e0" }}>
                          {items.map((item, idx) => (
                            <li key={idx} style={{ fontSize: "11px", marginBottom: "2px" }} dangerouslySetInnerHTML={{ __html: renderMarkdownWithCheckbox(item.title) }}></li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default ContentPlanningTab;
